// The aspect / slope composite colour model, mirrored from the pipeline's
// AspectSlopeComboColormap (web-server/ops/dem_processing/visualization/
// colormaps.go). The hue comes from the direction the ground faces and the
// paleness from its slope class, so the legend is a wheel: angle = aspect,
// ring = slope class, flat at the centre. Both sides pin the same colours in
// their tests (aspect_slope_combo_test.go and __tests__/aspectSlopeComposite),
// so a change on one side fails until the other follows.
//
// Dependency-free so node --test can load it directly.
// The pipeline's white-overlay opacity per slope bin. Colour strength is
// 1 - opacity, computed the same way so the floating-point result matches.
const WHITE_OVERLAY_OPACITY = [0.978, 0.8, 0.711, 0.636, 0.542, 0.436, 0.33, 0.223, 0.117, 0.0];
const CLASS_DEFS = [
    { key: 'flat', label: 'Flat', abbreviation: 'F', minDeg: 0, maxDeg: 2 },
    { key: 'gentle', label: 'Gentle Slope', abbreviation: 'GSl', minDeg: 2, maxDeg: 4 },
    { key: 'moderate', label: 'Moderate Slope', abbreviation: 'MSl', minDeg: 4, maxDeg: 8 },
    { key: 'strong', label: 'Strong Slope', abbreviation: 'SSl', minDeg: 8, maxDeg: 12 },
    { key: 'moderately_steep', label: 'Moderately Steep', abbreviation: 'MSt', minDeg: 12, maxDeg: 18 },
    { key: 'steep', label: 'Steep', abbreviation: 'St', minDeg: 18, maxDeg: 24 },
    { key: 'very_steep', label: 'Very Steep', abbreviation: 'VSt', minDeg: 24, maxDeg: 30 },
    { key: 'extremely_steep', label: 'Extremely Steep', abbreviation: 'ExSt-1', minDeg: 30, maxDeg: 36 },
    { key: 'excessively_steep', label: 'Excessively Steep', abbreviation: 'ExSt-2', minDeg: 36, maxDeg: 45 },
    { key: 'hazardously_steep', label: 'Hazardously Steep', abbreviation: 'HSt', minDeg: 45, maxDeg: 90 },
];
// Ordered flat (centre of the wheel) to hazardously steep (rim).
export const COMPOSITE_SLOPE_CLASSES = CLASS_DEFS.map((c, i) => ({
    ...c,
    colorStrength: 1.0 - WHITE_OVERLAY_OPACITY[i],
}));
const HAZARD_START_DEG = 45;
// Anchor hues every 45°, closing the loop at 360°. Southern flips the wheel so
// the sun-facing side keeps the warm colours.
const ANCHORS = {
    N: [
        [0, 0, 128],
        [65, 105, 225],
        [0, 128, 0],
        [255, 255, 0],
        [255, 165, 0],
        [255, 0, 0],
        [128, 0, 128],
        [137, 207, 240],
        [0, 0, 128],
    ],
    S: [
        [255, 165, 0],
        [255, 255, 0],
        [0, 128, 0],
        [65, 105, 225],
        [0, 0, 128],
        [137, 207, 240],
        [128, 0, 128],
        [255, 0, 0],
        [255, 165, 0],
    ],
};
const ANCHOR_STEP_DEG = 45;
// Per-degree hue table (0–360), interpolated exactly as the pipeline does.
const buildHueLookup = (hemisphere) => {
    const anchors = ANCHORS[hemisphere].map(([r, g, b]) => [r / 255.0, g / 255.0, b / 255.0]);
    const lookup = [];
    for (let degree = 0; degree <= 360; degree++) {
        let i1 = 0;
        let i2 = 0;
        for (let i = 0; i < anchors.length - 1; i++) {
            if (degree >= i * ANCHOR_STEP_DEG && degree <= (i + 1) * ANCHOR_STEP_DEG) {
                i1 = i;
                i2 = i + 1;
                break;
            }
        }
        const t = (degree - i1 * ANCHOR_STEP_DEG) / ANCHOR_STEP_DEG;
        lookup.push([
            anchors[i1][0] * (1 - t) + anchors[i2][0] * t,
            anchors[i1][1] * (1 - t) + anchors[i2][1] * t,
            anchors[i1][2] * (1 - t) + anchors[i2][2] * t,
        ]);
    }
    return lookup;
};
const HUE_LOOKUP = {
    N: buildHueLookup('N'),
    S: buildHueLookup('S'),
};
// The slope class a slope (degrees) falls in, matching the pipeline's binning:
// lower bound inclusive, and everything from 45° up is hazardously steep.
export const compositeSlopeClassIndex = (slopeDeg) => {
    const s = Math.min(90, Math.max(0, slopeDeg));
    if (s >= HAZARD_START_DEG)
        return COMPOSITE_SLOPE_CLASSES.length - 1;
    const idx = COMPOSITE_SLOPE_CLASSES.findIndex((c) => s >= c.minDeg && s < c.maxDeg);
    return idx === -1 ? 0 : idx;
};
// The composite colour for one aspect / slope pair as RGBA bytes. An aspect
// outside 0–360 (or NaN) is transparent, as on the map.
export const compositeColor = (hemisphere, aspectDeg, slopeDeg) => {
    if (Number.isNaN(aspectDeg) || Number.isNaN(slopeDeg) || aspectDeg < 0 || aspectDeg > 360) {
        return [0, 0, 0, 0];
    }
    const strength = COMPOSITE_SLOPE_CLASSES[compositeSlopeClassIndex(slopeDeg)].colorStrength;
    const [r, g, b] = HUE_LOOKUP[hemisphere][Math.round(aspectDeg) % 360];
    const blend = (v) => Math.trunc((v * strength + (1.0 - strength)) * 255);
    return [blend(r), blend(g), blend(b), 255];
};
const toHex = ([r, g, b]) => `#${[r, g, b].map((v) => v.toString(16).padStart(2, '0')).join('')}`;
// One ring of the wheel unrolled into a strip: its colour at each anchor
// direction (N, NE … back to N), for a CSS linear-gradient.
export const compositeRingColors = (hemisphere, classIndex) => {
    const slope = COMPOSITE_SLOPE_CLASSES[classIndex].minDeg;
    const colors = [];
    for (let deg = 0; deg <= 360; deg += ANCHOR_STEP_DEG) {
        colors.push(toHex(compositeColor(hemisphere, deg, slope)));
    }
    return colors;
};
// The whole wheel as RGBA pixels for a square canvas of sizePx: aspect is the
// clockwise angle from north (up), and the radius splits into ten equal rings,
// flat at the centre. Pixels outside the circle are transparent.
export const compositeWheelPixels = (hemisphere, sizePx) => {
    const pixels = new Uint8ClampedArray(sizePx * sizePx * 4);
    for (let y = 0; y < sizePx; y++) {
        for (let x = 0; x < sizePx; x++) {
            const point = wheelPointAt(x + 0.5, y + 0.5, sizePx);
            if (!point)
                continue;
            const slope = COMPOSITE_SLOPE_CLASSES[point.classIndex].minDeg;
            const [r, g, b, a] = compositeColor(hemisphere, point.aspectDeg, slope);
            const o = (y * sizePx + x) * 4;
            pixels[o] = r;
            pixels[o + 1] = g;
            pixels[o + 2] = b;
            pixels[o + 3] = a;
        }
    }
    return pixels;
};
// What a point on a wheel of sizePx (origin top-left) reads as, or null
// outside the wheel.
export const wheelPointAt = (x, y, sizePx) => {
    const radius = sizePx / 2;
    const dx = x - radius;
    // Screen y grows downward; north is up. 0 - dy keeps the centre at +0 so
    // atan2 reads it as 0° rather than 180°.
    const dy = y - radius;
    const north = 0 - dy;
    const r = Math.sqrt(dx * dx + dy * dy) / radius;
    if (r > 1)
        return null;
    const rings = COMPOSITE_SLOPE_CLASSES.length;
    const classIndex = Math.min(rings - 1, Math.floor(r * rings));
    const aspectDeg = ((Math.atan2(dx, north) * 180) / Math.PI + 360) % 360;
    return { aspectDeg, classIndex };
};
const DIRECTIONS = ['north', 'northeast', 'east', 'southeast', 'south', 'southwest', 'west', 'northwest'];
export const aspectDirectionName = (aspectDeg) => {
    const b = ((aspectDeg % 360) + 360) % 360;
    if (b >= 337.5 || b < 22.5)
        return DIRECTIONS[0];
    return DIRECTIONS[Math.min(7, Math.floor((b - 22.5) / 45) + 1)];
};
const gradePercent = (deg) => Math.round(Math.tan((deg * Math.PI) / 180) * 100);
// "18–24° (32–45%)"; the open-ended top class reads "45°+ (100%+)".
export const compositeSlopeRangeLabel = (classIndex) => {
    const c = COMPOSITE_SLOPE_CLASSES[classIndex];
    if (c.minDeg >= HAZARD_START_DEG)
        return `${c.minDeg}°+ (${gradePercent(c.minDeg)}%+)`;
    return `${c.minDeg}–${c.maxDeg}° (${gradePercent(c.minDeg)}–${gradePercent(c.maxDeg)}%)`;
};
// The plain-language reading of a wheel point, as two short lines so the
// readout keeps a fixed size. Flat ground has no meaningful facing direction.
export const describeWheelPoint = (point) => {
    const c = COMPOSITE_SLOPE_CLASSES[point.classIndex];
    return {
        facing: point.classIndex === 0 ? 'Level ground' : `Faces ${aspectDirectionName(point.aspectDeg)}`,
        slope: `${c.label} · ${compositeSlopeRangeLabel(point.classIndex)}`,
    };
};
// The pipeline names the composite artifact "Aspect slope composite"; the
// folder and processing-status label read "Aspect / Slope Composite". Either
// spelling is the composite.
const COMPOSITE_LAYER_NAMES = new Set(['aspect slope composite', 'aspect / slope composite']);
export const isCompositeLayerName = (name) => COMPOSITE_LAYER_NAMES.has((name || '').trim().toLowerCase());
// The statistics response spells the hemisphere out ("northern" /
// "southern"); anything else is unknown.
export const parseStatsHemisphere = (raw) => {
    const v = (raw || '').trim().toLowerCase();
    if (v === 'northern')
        return 'N';
    if (v === 'southern')
        return 'S';
    return null;
};
